import React, { useEffect } from 'react';

import classNames from 'classnames';
import Button from 'components/cosmos/button/button';
import Dropdown from 'components/cosmos/dropdown';
import { translate } from 'components/translate';
import {
  EVENT_ACTIONS,
  EVENTS_INTERACTIONS,
  EVENTS_CATEGORY,
  EVENTS_LABELS
} from 'modules/app/services/datahub/constants';
import DatahubService from 'modules/app/services/datahub/DatahubService';
import { buildInteractionEventData } from 'modules/app/services/datahub/helper';
import {
  MAX_LIBERATION_DAYS,
  MAX_LIBERATION_DAYS_IN_YEARS
} from 'modules/content/constants';
import PropTypes from 'prop-types';

import { SCHEDULER_OPTIONS, PREVIOUS, BEGIN } from './constants';
import SchedulerHeader from './SchedulerHeader';

import {
  clickSaveCustomDays,
  clickSaveCustomExpiration
} from '../DrippingBulkActionEvents';

import './CustomDaysSelector.scss';

const MAX_DAYS = 99999;

export const SCHEMA = {
  days: [
    'required',
    { name: 'string.length.max', when: 'onChange', params: { max: 8 } }
  ]
};

const CustomDaysSelector = ({
  type,
  checkedContent,
  t,
  transition,
  toogle,
  updateWizardState
}) => {
  const [sending, setSending] = React.useState(false);
  const [days, setDays] = React.useState('');
  const { title, description, request } = SCHEDULER_OPTIONS[type] || {};
  const isLiberationDaysExceeded = days > MAX_LIBERATION_DAYS;

  const sendEvents = (days) => {
    if (type === 'EXPIRATION') {
      clickSaveCustomExpiration(days);

      DatahubService.sendEvent({
        action: EVENT_ACTIONS.INTERACT,
        event: buildInteractionEventData({
          category:
            EVENTS_CATEGORY.CONTENT.CONTENT_SCHEDULE
              .CONTENT_SCHEDULE_EXPIRATION,
          action: EVENTS_INTERACTIONS.CLICK,
          label:
            EVENTS_LABELS.CONTENT.CONTENT_SCHEDULE.CUSTOM_DAYS_SAVE.replace(
              /(\{\{\w*\}\})/i,
              days
            )
        })
      });
    } else {
      clickSaveCustomDays(days);

      DatahubService.sendEvent({
        action: EVENT_ACTIONS.INTERACT,
        event: buildInteractionEventData({
          category:
            EVENTS_CATEGORY.CONTENT.CONTENT_SCHEDULE
              .CONTENT_SCHEDULE_DRIPPING_BY_DAY,
          action: EVENTS_INTERACTIONS.CLICK,
          label:
            EVENTS_LABELS.CONTENT.CONTENT_SCHEDULE.CUSTOM_DAYS_SAVE.replace(
              /(\{\{\w*\}\})/i,
              days
            )
        })
      });
    }
  };

  const goToPrevious = (e) => {
    e.stopPropagation();

    updateWizardState && updateWizardState(PREVIOUS);

    transition(PREVIOUS, { type });
  };

  const goToBegin = () => {
    updateWizardState && updateWizardState(BEGIN);

    transition(BEGIN, { type });
  };

  const closeDropdown = () => {
    toogle && toogle();

    Dropdown.closeMenu();

    goToBegin();
  };

  const clickInput = (e) => {
    e.stopPropagation();
  };

  const updateDays = (event) => {
    event.stopPropagation();

    setDays(event?.target?.value || 0);
  };

  const setDripping = async (e) => {
    e.stopPropagation();

    try {
      setSending(true);
      if (request) {
        await request(days, checkedContent);
      }
      closeDropdown();
    } finally {
      setSending(false);
      sendEvents(days);
      setDays('');
    }
  };

  useEffect(() => {
    setDays('');
  }, [type]);

  return (
    <>
      <SchedulerHeader
        goToPrevious={goToPrevious}
        description={description}
        closeDropdown={closeDropdown}
        title={title}
      />
      <Dropdown.MenuItem className="custom-days-selector__container _pb-4 _pt-4 _pl-5 _pr-5">
        <input
          className={classNames('hot-form__input hot-form__input--md', {
            'is-invalid': isLiberationDaysExceeded
          })}
          data-test="dripping-days-input"
          type="number"
          name="days"
          min="0"
          max={MAX_DAYS}
          placeholder={t('dripping.days_input_new')}
          value={days}
          onChange={updateDays}
          onClick={clickInput}
        />
        {isLiberationDaysExceeded && (
          <p className="_d-flex _mt-2 _text-1 _text-red-dark _text-wrap">
            {t('online_event.dripping.days.description', {
              data: {
                years: MAX_LIBERATION_DAYS_IN_YEARS,
                days: MAX_LIBERATION_DAYS
              }
            })}
          </p>
        )}
        <Button
          data-test="dripping-days-save"
          variation="primary"
          className="_mt-6"
          disabled={!days || isLiberationDaysExceeded}
          loading={sending}
          type="submit"
          onClick={setDripping}
          block
        >
          {t('general.save')}
        </Button>
      </Dropdown.MenuItem>
    </>
  );
};

CustomDaysSelector.propTypes = {
  type: PropTypes.string,
  t: PropTypes.func,
  checkedContent: PropTypes.object,
  transition: PropTypes.func,
  toogle: PropTypes.func,
  updateWizardState: PropTypes.func
};

export default translate()(CustomDaysSelector);
